import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { ADVISOR_NAV } from "@/pages/advisor/AdvisorHome";
import { EMPLOYER_NAV } from "@/pages/employer/EmployerHome";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, CheckCircle2, RotateCcw, CalendarClock } from "lucide-react";

const navFor = (role) => ({ student: STUDENT_NAV, advisor: ADVISOR_NAV, faculty: ADVISOR_NAV, employer: EMPLOYER_NAV, admin: ADMIN_NAV }[role] || STUDENT_NAV);
const STATUS_CLS = { open: "bg-amber-100 text-amber-800", in_progress: "bg-sky-100 text-sky-800", waiting: "bg-purple-100 text-purple-800", completed: "bg-emerald-100 text-emerald-800", cancelled: "bg-slate-100 text-slate-500" };
const STATUS_LABEL = { open: "Open", in_progress: "In Progress", waiting: "Waiting", completed: "Completed", cancelled: "Cancelled" };

export default function MyTasks() {
  const { user } = useAuth();
  const canAssign = ["admin", "advisor", "faculty"].includes(user?.real_role || user?.role);
  const [tab, setTab] = useState("open");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "normal", assigned_to: "" });
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);

  const TABS = [["open", "Open"], ["assigned_to_me", "Assigned to me"], ["created_by_me", "Personal"], ...(canAssign ? [["assigned_by_me", "Assigned by me"]] : []), ["completed", "Completed"]];

  const load = () => api.get("/tasks", { params: { filter: tab } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(null); load(); }, [tab]);
  useEffect(() => { if (canAssign) api.get("/tasks/assignable-users").then(({ data }) => setPeople(data)).catch(() => {}); }, [canAssign]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tasks", { ...form, assigned_to: form.assigned_to || undefined, due_date: form.due_date || undefined });
      toast.success(form.assigned_to ? "Task assigned" : "Task created");
      setOpen(false); setForm({ title: "", description: "", due_date: "", priority: "normal", assigned_to: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save task"); }
    finally { setBusy(false); }
  };
  const setStatus = async (t, status) => {
    try { await api.patch(`/tasks/${t.id}`, { status }); load(); toast.success(STATUS_LABEL[status]); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const overdue = (t) => t.due_date && t.status !== "completed" && t.status !== "cancelled" && t.due_date < new Date().toISOString().slice(0, 10);

  return (
    <DashboardShell title="My Tasks" subtitle="Your personal to-dos and tasks assigned to you" nav={navFor(user?.real_role || user?.role)}>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tasks-tab-${v}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === v ? "bg-[#003057] text-white" : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-100"}`}>{l}</button>
        ))}
        {!user?.preview && <Button onClick={() => setOpen(true)} data-testid="new-task-btn" className="ml-auto rounded-full bg-[#78be21] text-[#003057] hover:bg-[#6aa81c]"><Plus className="mr-1.5 h-4 w-4" /> New Task</Button>}
      </div>

      {!rows && <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003057]" /></div>}
      {rows && (
        <div className="space-y-2">
          {rows.map((t) => (
            <Card key={t.id} className="flex items-center gap-3 p-4 card-elevate" data-testid={`task-row-${t.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold text-[#003057]">{t.title}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_CLS[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                  {t.source === "assigned" && t.assigned_by_name && <span className="text-[11px] text-slate-400">from {t.assigned_by_name}</span>}
                  {t.source === "assigned" && t.assigned_by && t.owner_name && t.assigned_by !== t.owner_id && tab === "assigned_by_me" && <span className="text-[11px] text-slate-400">→ {t.owner_name}</span>}
                </div>
                {t.description && <p className="mt-0.5 truncate text-sm text-slate-500">{t.description}</p>}
                {t.due_date && <p className={`mt-0.5 flex items-center gap-1 text-xs ${overdue(t) ? "font-semibold text-red-600" : "text-slate-400"}`}><CalendarClock className="h-3 w-3" /> Due {t.due_date}{overdue(t) ? " · overdue" : ""}</p>}
              </div>
              {t.status !== "completed" ? (
                <Button size="sm" variant="outline" onClick={() => setStatus(t, "completed")} data-testid={`complete-${t.id}`}><CheckCircle2 className="mr-1.5 h-4 w-4 text-[#5fa114]" /> Complete</Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setStatus(t, "open")} data-testid={`reopen-${t.id}`}><RotateCcw className="mr-1.5 h-4 w-4" /> Reopen</Button>
              )}
            </Card>
          ))}
          {rows.length === 0 && <Card className="p-10 text-center text-slate-400 card-elevate" data-testid="tasks-empty">No tasks here yet.</Card>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-task-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003057]">New task</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required data-testid="task-title" className="mt-1.5" /></div>
            <div><Label>Description <span className="text-slate-400">(optional)</span></Label><Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="task-desc" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="task-due" className="mt-1.5" /></div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="task-priority"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {canAssign && (
              <div>
                <Label>Assign to <span className="text-slate-400">(optional — leave blank for a personal task)</span></Label>
                <Select value={form.assigned_to || "self"} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v === "self" ? "" : v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="task-assignee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself (personal task)</SelectItem>
                    {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.email}</SelectItem>)}
                  </SelectContent>
                </Select>
                {people.length === 0 && <p className="mt-1 text-[11px] text-slate-400">No one in your assignment scope yet.</p>}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#003057] hover:bg-[#00223f]" data-testid="task-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save task"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
